import { SignIn, SignUp } from "@clerk/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { queryKeys } from "../api";
import { usePanelContext } from "../panelContext";
import { AccountLoginErrorScreen, AuthShell } from "./ClerkAuthBoundary";
import { clerkAppearanceFlush } from "./clerkTheme";
import { SignupSection } from "./SignupSection";
import { usePortalAuthGate } from "./usePortalAuthGate";

export function ClerkSignInPage() {
  const { panelName } = usePanelContext();

  return (
    <AuthShell title={`Welcome to ${panelName || "your portal"}`}>
      <div className="signup-clerk-inline">
        <SignIn
          appearance={clerkAppearanceFlush}
          fallbackRedirectUrl="/"
          path="/sign-in"
          routing="path"
          signUpUrl="/sign-up"
        />
      </div>
    </AuthShell>
  );
}

export function ClerkSignUpPage() {
  return (
    <AuthShell
      copy="Create your sign-in account first, then complete your panel details."
      title="Sign up"
    >
      <div className="signup-clerk-inline">
        <SignUp
          appearance={clerkAppearanceFlush}
          forceRedirectUrl="/signup/details"
          path="/sign-up"
          routing="path"
          signInUrl="/sign-in"
        />
      </div>
    </AuthShell>
  );
}

export function SignupDetailsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    currentEmail,
    hasLoginError,
    isLoadingAccount,
    isMissingPanelist,
    isResettingSession,
    isSignedIn,
    panelId,
    resetSessionAndReturnHome,
  } = usePortalAuthGate();

  if (isLoadingAccount) {
    return (
      <AuthShell
        title="Loading account..."
        loading
        copy="Checking your account so we can finish your panel signup."
      />
    );
  }

  if (!isSignedIn) {
    return <Navigate replace to="/sign-in" />;
  }

  if (hasLoginError) {
    return (
      <AccountLoginErrorScreen
        isSigningOut={isResettingSession}
        onTryAgain={() => void resetSessionAndReturnHome()}
      />
    );
  }

  if (!isMissingPanelist) {
    return <Navigate replace to="/" />;
  }

  if (!currentEmail) {
    return (
      <AuthShell
        copy="Your account does not currently expose an email address for signup. Please sign in with a different account and try again."
        title="We need an email address"
      >
        <div className="auth-actions auth-actions-stack">
          <button
            className="btn btn-primary"
            disabled={isResettingSession}
            onClick={() => void resetSessionAndReturnHome()}
            type="button"
          >
            {isResettingSession ? "Signing out..." : "Use a different email"}
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <SignupSection
      currentEmail={currentEmail}
      onSuccess={() => {
        queryClient.removeQueries({ queryKey: queryKeys.panelist });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.sessionContext(panelId),
        });
        navigate("/");
      }}
      onCancel={() => resetSessionAndReturnHome()}
    />
  );
}
